import * as React from "react"

import type { Header } from "@tanstack/react-table"

import { MAX_RESIZE_WIDTH } from "./TableInternals"

/** Width (px) one arrow key press moves a column edge; Shift moves four times as far. */
const RESIZE_KEY_STEP = 16

/** Pointer travel (px) below which a press on the separator counts as a tap, not a drag. */
const TAP_SLOP = 3

interface ColumnDrag {
  pointerId: number
  startX: number
  startY: number
  startWidth: number
  moved: boolean
}

/**
 * Pointer and keyboard resizing for one column's header separator.
 *
 * Every gesture starts from the column's rendered width, so a flex column that
 * has stretched to fill the table doesn't jump back to its declared `size` on
 * the first drag or key press. Widths are clamped to the column's `minSize` and
 * to `MAX_RESIZE_WIDTH`, raised to the declared width, the minimum, or the width
 * a gesture starts from when any of those is wider.
 *
 * A tap on the separator (a press that never travels) and Enter call `onTap`,
 * which opens the step controls that resize the column without dragging.
 *
 * @param header - The column's TanStack header.
 * @param onTap - Called on a tap or Enter on the separator.
 * @returns Separator props (handlers and ARIA value attributes), whether a drag is live, and step and reset actions for the same column.
 */
export function useColumnResize<T>(
  header: Header<T, unknown>,
  onTap?: () => void,
) {
  const { column } = header
  const { table } = header.getContext()
  const dragRef = React.useRef<ColumnDrag | null>(null)
  const separatorRef = React.useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = React.useState(false)
  // Rendered width, tracked while the separator has focus and reported until
  // the column holds a dragged size.
  const [focusedWidth, setFocusedWidth] = React.useState<number | null>(null)

  const min = column.columnDef.minSize ?? 0
  const max = Math.max(MAX_RESIZE_WIDTH, column.columnDef.size ?? 0, min)
  const isSized = column.id in table.getState().columnSizing

  // `origin` is the width the gesture started from, so a column already wider
  // than the cap isn't snapped down by a small adjustment.
  const clamp = (width: number, origin: number) =>
    Math.round(Math.min(Math.max(max, origin), Math.max(min, width)))

  // The header cell is the separator's parent and spans exactly the column's
  // track. jsdom reports zero for it, so fall back to the declared size.
  const currentWidth = (handle: HTMLElement) => {
    if (isSized) {
      return column.getSize()
    }
    const rendered = handle.parentElement?.getBoundingClientRect().width ?? 0
    return rendered > 0 ? rendered : column.getSize()
  }

  const setWidth = (width: number, origin: number) => {
    table.setColumnSizing((sizing) => ({
      ...sizing,
      [column.id]: clamp(width, origin),
    }))
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // A second finger mid-drag must not restart the gesture from a new origin.
    if (dragRef.current || event.button !== 0) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: currentWidth(event.currentTarget),
      moved: false,
    }
    setIsDragging(true)
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (drag && drag.pointerId === event.pointerId) {
      if (
        !drag.moved &&
        Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) <
          TAP_SLOP
      ) {
        return
      }
      drag.moved = true
      setWidth(drag.startWidth + event.clientX - drag.startX, drag.startWidth)
    }
  }

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) {
      return
    }
    dragRef.current = null
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (!drag.moved && event.type === "pointerup") {
      onTap?.()
    }
  }

  // Steps from the width the separator reports, so a step from the popover and
  // an arrow key on the separator move a flex column from the same place.
  const resizeBy = (direction: 1 | -1, large = false) => {
    const handle = separatorRef.current
    const origin = handle ? currentWidth(handle) : column.getSize()
    const step = large ? RESIZE_KEY_STEP * 4 : RESIZE_KEY_STEP
    setWidth(origin + direction * step, origin)
  }

  const resetWidth = () => {
    table.setColumnSizing((sizing) => {
      const next = { ...sizing }
      delete next[column.id]
      return next
    })
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && onTap) {
      event.preventDefault()
      event.stopPropagation()
      onTap()
      return
    }
    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0
    if (direction === 0) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    const step = event.shiftKey ? RESIZE_KEY_STEP * 4 : RESIZE_KEY_STEP
    const origin = currentWidth(event.currentTarget)
    setWidth(origin + direction * step, origin)
  }

  const onFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    setFocusedWidth(Math.round(currentWidth(event.currentTarget)))
  }

  const onBlur = () => {
    setFocusedWidth(null)
  }

  // Keep the reported width current while focused: a flex column reflows when
  // the viewport or a docked panel changes the table's width.
  const isFocused = focusedWidth !== null
  React.useEffect(() => {
    const cell = separatorRef.current?.parentElement
    if (
      !isFocused ||
      isSized ||
      !cell ||
      typeof ResizeObserver === "undefined"
    ) {
      return
    }
    const observer = new ResizeObserver(() => {
      const width = cell.getBoundingClientRect().width
      if (width > 0) {
        setFocusedWidth(Math.round(width))
      }
    })
    observer.observe(cell)
    return () => observer.disconnect()
  }, [isFocused, isSized])

  const valueNow = isSized
    ? column.getSize()
    : (focusedWidth ?? column.getSize())

  return {
    isDragging,
    resizeBy,
    resetWidth,
    isSized,
    separatorProps: {
      ref: separatorRef,
      "aria-valuemin": min,
      "aria-valuemax": Math.max(max, valueNow),
      "aria-valuenow": valueNow,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
      onFocus,
      onBlur,
      // A click on the separator never reaches the header's sort control.
      onClick: (event: React.MouseEvent) => event.stopPropagation(),
    },
  }
}
